import { Add, Folder, InfoOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  createFilterOptions,
  FilterOptionsState,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { autocompleteHelp, NO_CATEGORY_LABEL } from '../constants';
import { PendingLabel } from '../types';
import {
  hasName,
  includesText,
  isSameName,
  parseCategoryLabel,
  splitCategoryLabel
} from '../utils/matching';
import {
  buildCategoryLabelOption,
  CategoryLookup,
  describeOption,
  isNewCategory
} from '../utils/options';

type Props = {
  isPickerOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  groupedOptions: PendingLabel[];
  categoryBadges: Record<string, string>;
  catalogLabels: PendingLabel[];
  isTaken: (name: string) => boolean;
  isUnavailable: (labelId: number) => boolean;
  lookups: CategoryLookup;
  onAdd: (option: PendingLabel) => void;
  isAssigned: (name: string) => boolean;
  isPending: (name: string) => boolean;
};

const filter = createFilterOptions<PendingLabel>({
  stringify: ({ categoryName, name }) => `${categoryName ?? ''} ${name}`
});

const HELP_TOOLTIP_SX = {
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow: 4,
  maxWidth: 320,
  p: 2,
  fontSize: 14,
  '& .MuiTooltip-arrow::before': {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider'
  }
};

const GroupHeader = ({ group, badge }: { group: string; badge?: string }) => {
  const isUncategorized = group === NO_CATEGORY_LABEL;

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1 }}>
      {!isUncategorized && (
        <Folder sx={{ fontSize: 16, color: 'text.secondary' }} />
      )}
      <Typography
        variant={isUncategorized ? 'caption' : 'subtitle2'}
        fontWeight={700}
        color={isUncategorized ? 'text.secondary' : 'text.primary'}
        sx={isUncategorized ? { letterSpacing: 0.5 } : undefined}
      >
        {group}
      </Typography>
      {badge && (
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: 11,
            fontWeight: 700,
            color: 'text.secondary',
            bgcolor: 'action.selected'
          }}
        >
          {badge}
        </Box>
      )}
    </Stack>
  );
};

const HelpIcon = () => (
  <Tooltip
    title={autocompleteHelp}
    placement="bottom-end"
    arrow
    slotProps={{ tooltip: { sx: HELP_TOOLTIP_SX } }}
  >
    <InfoOutlined sx={{ fontSize: 16, color: 'gray', mr: 1, cursor: 'help' }} />
  </Tooltip>
);

export const LabelAutocomplete = ({
  isPickerOpen,
  onOpenChange,
  inputValue,
  onInputValueChange,
  groupedOptions,
  categoryBadges,
  catalogLabels,
  isTaken,
  isUnavailable,
  lookups,
  onAdd,
  isAssigned,
  isPending
}: Props) => {
  const filterOptions = (
    options: PendingLabel[],
    params: FilterOptionsState<PendingLabel>
  ) => {
    const parts = splitCategoryLabel(params.inputValue);

    if (parts?.categoryName) {
      const matches = options.filter(
        ({ categoryName, name }) =>
          categoryName !== undefined &&
          isSameName(categoryName, parts.categoryName) &&
          includesText(name, parts.labelName)
      );

      const parsed = parseCategoryLabel(params.inputValue);
      if (!parsed) return matches;

      const created = buildCategoryLabelOption(parsed, lookups);

      if (isTaken(created.name)) {
        return [
          ...matches,
          {
            name: created.name,
            alreadyAssigned: isAssigned(created.name),
            isPending: isPending(created.name)
          }
        ];
      }

      return hasName(matches, parsed.labelName)
        ? matches
        : [...matches, created];
    }

    const filtered = filter(options, params);
    const name = params.inputValue.trim();

    if (name && isTaken(name)) {
      return [
        ...filtered,
        {
          name,
          alreadyAssigned: isAssigned(name),
          isPending: isPending(name)
        }
      ];
    }

    return name && !isTaken(name) && !hasName(catalogLabels, name)
      ? [...filtered, { name }]
      : filtered;
  };

  const handleChange = (_: unknown, value: PendingLabel | string | null) => {
    onInputValueChange('');
    if (!value) return;

    if (typeof value !== 'string') {
      onAdd(value);
      return;
    }

    const parsed = parseCategoryLabel(value);
    onAdd(parsed ? buildCategoryLabelOption(parsed, lookups) : { name: value });
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      clearOnBlur
      selectOnFocus
      disableCloseOnSelect
      size="small"
      value={null}
      inputValue={inputValue}
      open={isPickerOpen}
      onOpen={() => onOpenChange(true)}
      onClose={() => onOpenChange(false)}
      onInputChange={(_, value) => onInputValueChange(value)}
      onChange={handleChange}
      options={groupedOptions}
      groupBy={(option) => {
        if (option.alreadyAssigned || option.isPending) return '';
        return option.categoryName ?? NO_CATEGORY_LABEL;
      }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.name
      }
      getOptionDisabled={(option) =>
        option.id !== undefined && isUnavailable(option.id)
      }
      filterOptions={filterOptions}
      renderGroup={(params) => (
        <li key={params.group}>
          {params.group && (
            <GroupHeader
              group={params.group}
              badge={categoryBadges[params.group]}
            />
          )}
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={({ key, ...props }, option) => (
        <li
          key={key}
          {...props}
          style={{ paddingLeft: isNewCategory(option) ? 16 : 32 }}
        >
          {describeOption(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Add a label - type to search or create"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <Add sx={{ fontSize: 16, color: 'gray' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                <HelpIcon />
              </>
            )
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
        />
      )}
    />
  );
};
